import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../config";

const LoginRegister = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    login_name: "",
    login_phone: "",
    login_address: "",
    login_email: "",
    login_password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError("");
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    setSuccess("");

    if (isLogin) {
      // Login payload
      const payload = {
        LOGIN_EMAIL: formData.login_email,
        LOGIN_PASSWORD: formData.login_password,
      };

      try {
        const response = await axios.post(`${API_BASE_URL}/check-login`, payload);
        if (response.data.success) {
          // Normalize keys for the user object to match expectations of other pages
          const rawUser = response.data.user;
          const userObj = {
            login_id: rawUser.LOGIN_ID,
            login_name: rawUser.LOGIN_NAME,
            login_email: rawUser.LOGIN_EMAIL,
            login_phone: rawUser.LOGIN_PHONE,
            login_address: rawUser.LOGIN_ADDRESS,
            name: rawUser.LOGIN_NAME,
            email: rawUser.LOGIN_EMAIL,
            phone: rawUser.LOGIN_PHONE,
            address: rawUser.LOGIN_ADDRESS,
            role: rawUser.ROLE || rawUser.role || "user",
          };

          localStorage.setItem("user", JSON.stringify(userObj));
          localStorage.setItem("token", response.data.token);

          setSuccess("Login successful! Redirecting...");
          setTimeout(() => {
            if (userObj.role === "admin") {
              window.location.href = "/admin";
            } else {
              window.location.href = "/";
            }
          }, 1500);
        } else {
          setError(response.data.error || "Invalid Email or Password");
        }
      } catch (err) {
        setError(err.response?.data?.error || "Invalid Email or Password");
      } finally {
        setIsLoading(false);
      }
    } else {
      // Registration payload
      const payload = {
        LOGIN_NAME: formData.login_name,
        LOGIN_PHONE: formData.login_phone,
        LOGIN_ADDRESS: formData.login_address,
        LOGIN_EMAIL: formData.login_email,
        LOGIN_PASSWORD: formData.login_password,
      };

      // Basic front-end validations
      if (!payload.LOGIN_NAME || !payload.LOGIN_EMAIL || !payload.LOGIN_PHONE || !payload.LOGIN_ADDRESS || !payload.LOGIN_PASSWORD) {
        setError("All fields are required for registration.");
        setIsLoading(false);
        return;
      }

      if (!/^[0-9]{10}$/.test(payload.LOGIN_PHONE)) {
        setError("Please enter a valid 10-digit mobile number.");
        setIsLoading(false);
        return;
      }

      try {
        const response = await axios.post(`${API_BASE_URL}/register`, payload);
        if (response.data.success) {
          setSuccess("Account created successfully! Please sign in.");
          setIsLogin(true);
          // Keep email and password but clear others
          setFormData((prev) => ({
            ...prev,
            login_name: "",
            login_phone: "",
            login_address: "",
          }));
        } else {
          setError(response.data.error || "Registration failed. Try again.");
        }
      } catch (err) {
        setError(err.response?.data?.error || "Registration failed. Email might already exist.");
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-orange-100 py-12 px-4 sm:px-6 lg:px-8 font-sans">
      <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-3xl shadow-xl border border-orange-100">
        <div>
          <h2 className="mt-2 text-center text-3xl font-extrabold text-red-950 font-serif">
            {isLogin ? "Welcome Back!" : "Create Your Account"}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-500">
            {isLogin ? "Sign in to manage orders & track purchases" : "Join us for premium crackers at wholesale prices"}
          </p>
        </div>

        {/* View Toggle pills */}
        <div className="flex bg-orange-50 p-1 rounded-2xl border border-orange-100">
          <button
            type="button"
            onClick={() => {
              setIsLogin(true);
              setError("");
              setSuccess("");
            }}
            className={`w-1/2 py-2 rounded-xl text-xs sm:text-sm font-bold transition-all duration-300 ${
              isLogin ? "bg-red-800 text-white shadow-md" : "text-gray-600 hover:text-red-800"
            }`}
          >
            Sign In
          </button>
          <button
            type="button"
            onClick={() => {
              setIsLogin(false);
              setError("");
              setSuccess("");
            }}
            className={`w-1/2 py-2 rounded-xl text-xs sm:text-sm font-bold transition-all duration-300 ${
              !isLogin ? "bg-red-800 text-white shadow-md" : "text-gray-600 hover:text-red-800"
            }`}
          >
            Sign Up
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 p-3 rounded-lg text-sm text-red-700 font-medium">
            ⚠️ {error}
          </div>
        )}

        {success && (
          <div className="bg-green-50 border-l-4 border-green-500 p-3 rounded-lg text-sm text-green-700 font-medium">
            ✅ {success}
          </div>
        )}

        <form className="mt-6 space-y-4" onSubmit={handleFormSubmit}>
          {!isLogin && (
            <>
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider">Full Name</label>
                <input
                  type="text"
                  name="login_name"
                  value={formData.login_name}
                  onChange={handleInputChange}
                  required
                  placeholder="e.g. John Doe"
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-800 text-sm"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider">Mobile Number</label>
                  <input
                    type="tel"
                    name="login_phone"
                    value={formData.login_phone}
                    onChange={handleInputChange}
                    required
                    placeholder="10-digit number"
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-800 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider">Email Address</label>
                  <input
                    type="email"
                    name="login_email"
                    value={formData.login_email}
                    onChange={handleInputChange}
                    required
                    placeholder="email@example.com"
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-800 text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider">Shipping Address</label>
                <textarea
                  name="login_address"
                  value={formData.login_address}
                  onChange={handleInputChange}
                  required
                  rows="3"
                  placeholder="Enter full delivery address"
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-800 text-sm"
                />
              </div>
            </>
          )}

          {isLogin && (
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider">Email Address</label>
              <input
                type="email"
                name="login_email"
                value={formData.login_email}
                onChange={handleInputChange}
                required
                placeholder="email@example.com"
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-800 text-sm"
              />
            </div>
          )}

          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider">Password</label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                name="login_password"
                value={formData.login_password}
                onChange={handleInputChange}
                required
                placeholder="••••••••"
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-800 text-sm pr-12"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-4 flex items-center text-xs font-bold text-gray-500 hover:text-red-800"
              >
                {showPassword ? "HIDE" : "SHOW"}
              </button>
            </div>
          </div>

          <div className="pt-2">
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-red-900 hover:bg-red-800 text-white font-bold py-3 rounded-xl transition duration-300 shadow-md hover:shadow-lg disabled:bg-gray-400 flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  <span>Processing...</span>
                </>
              ) : (
                <span>{isLogin ? "Sign In" : "Register Account"}</span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LoginRegister;
